import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ip_checkAdminAccess } from '../lib/ip_admin_guard';
import { supabase } from '../lib/supabase';

interface AdminContextType {
  adminName: string;
  handleLogout: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType | null>(null);

export function AdminProvider({ children }: { children: ReactNode }) {
  const [adminName, setAdminName] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Keep a ref to the current pathname so the auth subscription never needs
  // to re-register when the route changes. Re-registering onAuthStateChange on
  // every navigation was the root cause of the Quotes → Invoices redirect bug:
  // each re-registration fires a new INITIAL_SESSION event, which could arrive
  // with a momentarily null session and force an unwanted redirect.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  });

  useEffect(() => {
    const loadAdminData = async () => {
      const hasAccess = await ip_checkAdminAccess();
      if (hasAccess) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          // 1. Try user_metadata first (set during signup)
          const metaName = session.user.user_metadata?.full_name || session.user.user_metadata?.name;
          if (metaName && typeof metaName === 'string' && metaName.trim()) {
            setAdminName(metaName.trim());
            return;
          }

          // 2. Try ip_admin_users table
          const { data: adminRow } = await supabase
            .from('ip_admin_users')
            .select('full_name')
            .eq('auth_user_id', session.user.id)
            .maybeSingle();

          if (adminRow?.full_name?.trim()) {
            setAdminName(adminRow.full_name.trim());
            return;
          }

          // 3. Fallback: use email (but just the part before @)
          if (session.user.email) {
            const nameFromEmail = session.user.email.split('@')[0];
            const pretty = nameFromEmail
              .replace(/[._]/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase());
            setAdminName(pretty);
          }
        }
      }
    };
    loadAdminData();
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // ONLY redirect on explicit SIGNED_OUT — this is the only event that
        // reliably means the user has actually lost their session.
        //
        // INITIAL_SESSION fires every time this subscription is re-registered
        // (which used to happen on every route change). Even if we keep the
        // subscription stable (via pathnameRef), INITIAL_SESSION can still fire
        // with session=null during a brief hydration window. We must NOT treat
        // that as a real sign-out, or every navigation between admin pages can
        // redirect to login and then to /admin/dashboard instead of the intended
        // route (reproducing the "Quotes → Invoices" bug).
        //
        // TOKEN_REFRESHED with null session is similarly unreliable as a logout
        // signal — the token may simply be in the middle of refreshing.
        if (event === 'SIGNED_OUT' && !session) {
          setAdminName('');
          if (pathnameRef.current.startsWith('/admin')) {
            navigate('/admin/login', { replace: true });
          }
          return;
        }

        // Update admin name whenever a valid session is confirmed
        if (session?.user) {
          const metaName = session.user.user_metadata?.full_name || session.user.user_metadata?.name;
          if (metaName && typeof metaName === 'string' && metaName.trim()) {
            setAdminName(metaName.trim());
          } else if (session.user.email) {
            const nameFromEmail = session.user.email.split('@')[0];
            const pretty = nameFromEmail.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            setAdminName(pretty);
          }
        }
      },
    );
    return () => {
      subscription.unsubscribe();
    };
    // ← Empty deps: register subscription ONCE, use pathnameRef for current path.
    // This prevents INITIAL_SESSION from re-firing on every route change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setAdminName('');
    navigate('/admin/login', { replace: true });
  };

  return (
    <AdminContext.Provider value={{ adminName, handleLogout }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin debe usarse dentro de AdminProvider');
  }
  return context;
}